import { useState } from "react";
import { Plus, Pencil, Trash2, Briefcase, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLoad, Loading, EmptyState, Panel, StatusBadge } from "./shared";
import { RecordEditor } from "./RecordEditor";

const SERVICE_FIELDS = [
  { key: "key", label: "Key (URL id)", required: true, lockOnEdit: true },
  { key: "title", label: "Title", required: true },
  { key: "description", label: "Description", type: "textarea", required: true },
  { key: "icon", label: "Icon (Package, Store, TrendingUp, Zap, Rocket, Sparkles)", default: "Rocket" },
  { key: "bullets", label: "Bullet points", type: "list", rows: 6 },
  { key: "order", label: "Display order", type: "number", default: 0 },
];
const PRICING_FIELDS = [
  { key: "key", label: "Key", required: true, lockOnEdit: true },
  { key: "name", label: "Package name", required: true },
  { key: "price_display", label: "Price display (e.g. Starting at £999)", required: true },
  { key: "price_amount", label: "Price amount (£)", type: "number" },
  { key: "frequency", label: "Frequency (one-time / monthly)", default: "one-time" },
  { key: "tagline", label: "Tagline", required: true },
  { key: "bullets", label: "Included features", type: "list", rows: 7 },
  { key: "cta_label", label: "Button label", default: "Get Started" },
  { key: "cta_href", label: "Button link", default: "/commerce/book-consultation" },
  { key: "is_featured", label: "Featured package", type: "switch" },
  { key: "order", label: "Display order", type: "number", default: 0 },
];
export const CASE_FIELDS = [
  { key: "title", label: "Title", required: true },
  { key: "industry", label: "Industry", required: true },
  { key: "challenge", label: "Challenge", type: "textarea", required: true },
  { key: "services", label: "Services used", type: "list", rows: 3 },
  { key: "approach", label: "Approach", type: "textarea", required: true },
  { key: "outcome", label: "Outcome", type: "textarea", required: true },
  { key: "is_illustrative", label: "Illustrative scenario (not a real client)", type: "switch" },
  { key: "is_published", label: "Published on website", type: "switch" },
  { key: "order", label: "Display order", type: "number", default: 0 },
];

export const useCrudList = (call, path, idKey) => {
  const { data, loading, reload } = useLoad(call, path);
  const save = (existing) => async (values) => {
    if (existing) await call(`${path}/${encodeURIComponent(existing[idKey])}`, { method: "PATCH", body: values });
    else await call(path, { method: "POST", body: values });
    toast.success("Saved — live on the public website"); reload();
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title || item.name}"?`)) return;
    await call(`${path}/${encodeURIComponent(item[idKey])}`, { method: "DELETE" });
    toast.success("Deleted"); reload();
  };
  return { items: Array.isArray(data) ? data : [], loading, save, remove };
};

const ItemCard = ({ title, sub, badges, onEdit, onDelete, testid }) => (
  <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-50 last:border-0" data-testid={testid}>
    <div className="min-w-0">
      <p className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">{title}{badges}</p>
      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{sub}</p>
    </div>
    <div className="flex gap-1.5 shrink-0">
      <Button size="sm" variant="outline" onClick={onEdit} data-testid={`${testid}-edit`}><Pencil className="h-3.5 w-3.5" /></Button>
      <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`${testid}-delete`}><Trash2 className="h-3.5 w-3.5 text-rose-500" /></Button>
    </div>
  </div>
);

export const CrudPanel = ({ call, path, idKey, fields, label, testid, renderItem }) => {
  const { items, loading, save, remove } = useCrudList(call, path, idKey);
  const [editing, setEditing] = useState(null);
  return (
    <Panel testid={testid} title={label} action={<Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setEditing({})} data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>}>
      {loading ? <Loading /> : items.length ? items.map(it => (
        <ItemCard key={it[idKey]} testid={`${testid}-item-${String(it[idKey]).replace(/\W+/g, "-").toLowerCase()}`} {...renderItem(it)}
          onEdit={() => setEditing(it)} onDelete={() => remove(it)} />
      )) : <div className="p-6"><EmptyState icon={Briefcase} title={`No ${label.toLowerCase()} yet`} /></div>}
      <RecordEditor open={!!editing} onOpenChange={o => !o && setEditing(null)} title={editing?.[idKey] ? `Edit ${label}` : `Add ${label}`}
        fields={fields} record={editing?.[idKey] ? editing : null} onSave={save(editing?.[idKey] ? editing : null)} testid={`${testid}-editor`} />
    </Panel>
  );
};

export const CommerceServicesPricing = ({ call }) => (
  <div className="grid xl:grid-cols-2 gap-6" data-testid="commerce-services-pricing">
    <CrudPanel call={call} path="/services" idKey="key" fields={SERVICE_FIELDS} label="Services" testid="services"
      renderItem={s => ({ title: s.title, sub: s.description })} />
    <CrudPanel call={call} path="/pricing" idKey="key" fields={PRICING_FIELDS} label="Pricing packages" testid="pricing"
      renderItem={p => ({ title: `${p.name} — ${p.price_display}`, sub: p.tagline,
        badges: p.is_featured && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" /> })} />
  </div>
);

export const CommerceCaseStudies = ({ call }) => (
  <CrudPanel call={call} path="/case-studies" idKey="title" fields={CASE_FIELDS} label="Case studies" testid="case-studies"
    renderItem={c => ({ title: c.title, sub: `${c.industry} · ${c.challenge}`,
      badges: <>{c.is_illustrative && <StatusBadge status="illustrative" />}<StatusBadge status={c.is_published ? "published" : "draft"} /></> })} />
);
